import React, { useEffect } from 'react'
import { Row, Col, Typography, Button, Spin } from 'antd'
import {
  PlusCircleOutlined,
  EditOutlined,
  ImportOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getProducts,
  getIsLoading,
  getModalVisibility,
  getImportTarget,
  ModalName,
  ProductActions,
} from '~/Store/Products'
import { ScrollBar } from '~/Components/ScrollBar'
import { NoData } from '~/Components/NoData'
import { IProduct } from '~/Services/Product.interface'
import { ProductFormModal } from './ProductFormModal'
import { ImportPermissionsModal } from './ImportPermissionsModal'
import styles from './Products.module.less'

export const AdminProducts = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const products = useAppSelector(getProducts)
  const isLoading = useAppSelector(getIsLoading)
  const formModalVisible = useAppSelector(getModalVisibility(ModalName.Product))
  const importTarget = useAppSelector(getImportTarget)

  useEffect(() => {
    dispatch(ProductActions.getProducts())
  }, [])

  const newProductClick = () => {
    dispatch(ProductActions.setProductSelected(undefined))
    dispatch(
      ProductActions.setModalVisibility({ name: ModalName.Product, visible: true }),
    )
  }

  const editProductClick = (product: IProduct) => {
    dispatch(ProductActions.setProductSelected(product))
    dispatch(
      ProductActions.setModalVisibility({ name: ModalName.Product, visible: true }),
    )
  }

  const importClick = (product: IProduct) => {
    dispatch(ProductActions.openImportModal(product))
  }

  // Product Detail is reached by passing the already-loaded IProduct through router state (same
  // convention as EditApiKey.tsx) — the summary header renders instantly with no extra API call.
  const viewProductClick = (product: IProduct) => {
    history.push('/Admin/Products/Detail', { product })
  }

  return (
    <div className={styles.Container}>
      {formModalVisible && <ProductFormModal />}
      {importTarget && <ImportPermissionsModal />}

      <Row align="stretch">
        <Typography.Title style={{ flex: 1 }} level={4}>
          {t('admin.products')}
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={newProductClick}
        >
          {t('admin.newProduct')}
        </Button>
      </Row>

      <Spin spinning={isLoading}>
        <ScrollBar
          style={{
            paddingRight: '16px',
            marginRight: '-16px',
            height: 'calc(100vh - 280px)',
          }}
        >
          <div className={styles.HeaderRow}>
            <Row align="middle">
              <Col xs={6} md={6}>{t('admin.name')}</Col>
              <Col xs={5} md={5}>{t('admin.clientId')}</Col>
              <Col xs={4} md={4}>{t('admin.permissionGroups')}</Col>
              <Col xs={4} md={4}>{t('admin.permissions')}</Col>
              <Col xs={5} md={5} style={{ textAlign: 'right' }}>
                {t('admin.actions')}
              </Col>
            </Row>
          </div>

          {products.length === 0 ? (
            <NoData content={t('admin.noProducts')} />
          ) : (
            products.map(product => (
              <div className={styles.Item} key={product.guid}>
                <Row align="middle">
                  <Col xs={6} md={6}>{product.name}</Col>
                  <Col xs={5} md={5}>{product.clientId}</Col>
                  <Col xs={4} md={4}>{product.permissionGroupCount}</Col>
                  <Col xs={4} md={4}>
                    <a onClick={() => viewProductClick(product)}>
                      {product.permissionCount}
                    </a>
                  </Col>
                  <Col xs={5} md={5}>
                    <div className={styles.Actions}>
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => viewProductClick(product)}
                      >
                        {t('admin.view')}
                      </Button>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => editProductClick(product)}
                      >
                        {t('admin.edit')}
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<ImportOutlined />}
                        onClick={() => importClick(product)}
                      >
                        {t('admin.importPermissions')}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            ))
          )}
        </ScrollBar>
      </Spin>
    </div>
  )
}
